/**
 * Dashboard Component
 * Main dashboard with shared filter bar for all analysis tabs
 * Supports consolidated (parent company) and single company views
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getCurrentUser, clearAuthData, getSelectedCompany, setSelectedCompany } from '../utils/auth';
import WeeklyDataMatrix from './WeeklyDataMatrix';
import UserTransactions from './UserTransactions';
import DispatchAnalysis from './DispatchAnalysis';
import BrokerAnalysis from './BrokerAnalysis';
import LaneAnalysis from './LaneAnalysis';
import '../styles/Dashboard.css';
import logo from '../image/logo_cut.png';

const ANALYSIS_TABS = ['dispatch', 'broker', 'lanes'];

const DATE_PRESETS = [
  ['This Week', 'this_week'], ['Last Week', 'last_week'], ['Last 30d', 'last_30'],
  ['This Month', 'this_month'], ['Last Month', 'last_month'], ['Last 90d', 'last_90'],
  ['YTD', 'ytd'], ['All', 'all'],
];

function Dashboard() {
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [parentCompanies, setParentCompanies] = useState([]);
  const [selectedCompany, setSelectedCompanyState] = useState('');
  const [selectedParentCompany, setSelectedParentCompany] = useState('');
  const [viewMode, setViewMode] = useState('single'); // 'consolidated' or 'single'
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cashflow');
  const navigate = useNavigate();

  // Shared filter state
  const [pickupDateStart, setPickupDateStart] = useState('');
  const [pickupDateEnd, setPickupDateEnd] = useState('');
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [lastNWeeks, setLastNWeeks] = useState('all');
  const [filterOptions, setFilterOptions] = useState({ dispatchers: [], brokers: [], drivers: [], available_weeks: [] });
  const [filterVersion, setFilterVersion] = useState(0);

  useEffect(() => {
    loadUserAndCompanies();
  }, []);

  const handleCompanyChange = (companyId) => {
    setSelectedCompanyState(companyId);
    setSelectedCompany(companyId);
    setSelectedWeeks([]);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setSelectedWeeks([]);
    if (mode === 'consolidated' && parentCompanies.length > 0 && !selectedParentCompany) {
      setSelectedParentCompany(parentCompanies[0].parent_company_id);
    }
  };

  const loadUserAndCompanies = async () => {
    try {
      const [userResponse, companiesResponse, parentResponse] = await Promise.all([
        api.get('/auth/me'),
        api.get('/data/my-companies'),
        api.get('/parent-companies'),
      ]);
      setUser(userResponse.data);
      const companyList = companiesResponse.data.companies || companiesResponse.data || [];
      setCompanies(companyList);
      setParentCompanies(parentResponse.data || []);

      const saved = getSelectedCompany();
      const valid = companyList.find(c => String(c.company_id) === String(saved));
      const initial = valid ? saved : (companyList.length > 0 ? companyList[0].company_id : '');
      setSelectedCompanyState(String(initial));
      if (initial) setSelectedCompany(String(initial));

      if (parentResponse.data?.length > 0) {
        setSelectedParentCompany(parentResponse.data[0].parent_company_id);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // The effective company_id for API calls: 0 when consolidated, actual ID when single
  const effectiveCompanyId = viewMode === 'consolidated' ? '0' : selectedCompany;

  // Load filter options when company/view changes
  useEffect(() => {
    if (effectiveCompanyId) {
      loadFilterOptions();
    }
  }, [effectiveCompanyId, selectedParentCompany, viewMode]);

  const loadFilterOptions = async () => {
    try {
      let url = `/loads/${effectiveCompanyId}/filter-options`;
      if (viewMode === 'consolidated' && selectedParentCompany) {
        url += `?parent_company_id=${selectedParentCompany}`;
      }
      const response = await api.get(url);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  };

  // Shared buildParams — child components can pass extra entity filters
  const buildParams = useCallback((extraFilters = {}) => {
    const params = new URLSearchParams();
    if (pickupDateStart) params.append('pickup_date_start', pickupDateStart);
    if (pickupDateEnd) params.append('pickup_date_end', pickupDateEnd);
    if (viewMode === 'consolidated' && selectedParentCompany) {
      params.append('parent_company_id', selectedParentCompany);
    }
    Object.entries(extraFilters).forEach(([k, v]) => { if (v) params.append(k, v); });
    return params.toString();
  }, [pickupDateStart, pickupDateEnd, viewMode, selectedParentCompany]);

  // Date presets
  const setDatePreset = (preset) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split('T')[0];
    const startOfWeek = (d) => { const day = d.getDay(); const diff = d.getDate() - day + (day === 0 ? -6 : 1); return new Date(d.getFullYear(), d.getMonth(), diff); };
    let start, end;
    switch (preset) {
      case 'this_week': start = startOfWeek(today); end = today; break;
      case 'last_week': { const s = startOfWeek(today); s.setDate(s.getDate() - 7); start = s; end = new Date(s); end.setDate(end.getDate() + 6); break; }
      case 'last_30': start = new Date(today); start.setDate(start.getDate() - 30); end = today; break;
      case 'this_month': start = new Date(today.getFullYear(), today.getMonth(), 1); end = today; break;
      case 'last_month': start = new Date(today.getFullYear(), today.getMonth() - 1, 1); end = new Date(today.getFullYear(), today.getMonth(), 0); break;
      case 'last_90': start = new Date(today); start.setDate(start.getDate() - 90); end = today; break;
      case 'ytd': start = new Date(today.getFullYear(), 0, 1); end = today; break;
      case 'all':
        setPickupDateStart(''); setPickupDateEnd(''); setSelectedWeeks([]);
        setFilterVersion(v => v + 1);
        return;
      default: return;
    }
    setPickupDateStart(fmt(start));
    setPickupDateEnd(fmt(end));
    setSelectedWeeks([]);
    setFilterVersion(v => v + 1);
  };

  const handleApplyFilters = () => {
    setFilterVersion(v => v + 1);
  };

  const handleClearFilters = () => {
    setPickupDateStart('');
    setPickupDateEnd('');
    setSelectedWeeks([]);
    setLastNWeeks('all');
    setFilterVersion(v => v + 1);
  };

  // Available weeks from API (sorted newest first from backend, reverse for display oldest-first)
  const availableWeeks = useMemo(() => {
    if (!filterOptions.available_weeks?.length) return [];
    return [...filterOptions.available_weeks].reverse().map(ws => {
      const start = new Date(ws + 'T00:00:00');
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { value: ws, label: formatWeekLabel(start, end) };
    });
  }, [filterOptions.available_weeks]);

  const toggleWeek = (weekVal) => {
    setSelectedWeeks(prev =>
      prev.includes(weekVal) ? prev.filter(w => w !== weekVal) : [...prev, weekVal]
    );
    setLastNWeeks('all');
  };

  const handleLastNWeeksChange = (value) => {
    setLastNWeeks(value);
    if (value === 'all') {
      setSelectedWeeks([]);
    } else {
      // Select the last N weeks (most recent first)
      const reversed = [...availableWeeks].reverse();
      const lastN = reversed.slice(0, parseInt(value)).map(w => w.value);
      setSelectedWeeks(lastN);
    }
  };

  const handleLogout = () => {
    clearAuthData();
    window.location.href = '/login';
  };

  // Companies under selected parent (for display)
  const companiesUnderParent = useMemo(() => {
    if (!selectedParentCompany) return [];
    return companies.filter(c => c.parent_company_id === selectedParentCompany);
  }, [companies, selectedParentCompany]);

  const showFilterBar = ANALYSIS_TABS.includes(activeTab);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="dashboard-header-inner">
          <div className="dashboard-brand">
            <div className="dashboard-logo">
              <img src={logo} alt="Wolf City Logo" />
            </div>
            <div>
              <h1 className="dashboard-title">
                Wolf City <span className="dashboard-title-accent">Financial</span>
              </h1>
            </div>
          </div>

          <div className="dashboard-actions">
            <div className="dashboard-user-info">
              <span>{user?.full_name || user?.email}</span>
              <span className="dashboard-user-badge">{user?.user_type}</span>
            </div>
            {user?.user_type === 'admin' && (
              <button className="dashboard-btn-admin" onClick={() => navigate('/admin')}>Admin</button>
            )}
            <button className="dashboard-btn-logout" onClick={handleLogout}>Logout</button>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="dashboard-nav">
        <div className="dashboard-nav-inner">
          {[
            ['cashflow', 'CashFlow'], ['dispatch', 'Dispatch'], ['broker', 'Broker'],
            ['lanes', 'Lane Analysis'], ['transactions', 'Transactions'],
          ].map(([key, label]) => (
            <button key={key} className={`dashboard-nav-btn ${activeTab === key ? 'active' : ''}`} onClick={() => setActiveTab(key)}>
              {label}
            </button>
          ))}
        </div>
      </nav>

      {/* Shared Filter Bar — visible for analysis tabs, matches WeeklyDataMatrix style */}
      {showFilterBar && (
        <div className="dashboard-main" style={{ paddingBottom: 0 }}>
          <div className="matrix-header">
            <h2>Load Analysis</h2>

            {/* Controls Row — same layout as WeeklyDataMatrix */}
            <div className="controls-row">
              <div className="controls-left">
                {/* View mode toggle */}
                <div className="view-mode-selector">
                  <label className={viewMode === 'consolidated' ? 'active' : ''}>
                    <input type="radio" value="consolidated" checked={viewMode === 'consolidated'} onChange={() => handleViewModeChange('consolidated')} />
                    <span>Consolidated</span>
                  </label>
                  <label className={viewMode === 'single' ? 'active' : ''}>
                    <input type="radio" value="single" checked={viewMode === 'single'} onChange={() => handleViewModeChange('single')} />
                    <span>Single Company</span>
                  </label>
                </div>

                {/* Company / Parent Company selector */}
                {viewMode === 'single' ? (
                  <select
                    className="company-selector"
                    value={selectedCompany}
                    onChange={(e) => handleCompanyChange(e.target.value)}
                  >
                    <option value="">-- Select Company --</option>
                    {companies.map(c => (
                      <option key={c.company_id} value={c.company_id}>
                        {c.company_name}
                        {c.parent_company_name && ` (${c.parent_company_name})`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                    <select
                      className="company-selector"
                      value={selectedParentCompany}
                      onChange={(e) => { setSelectedParentCompany(e.target.value); setSelectedWeeks([]); }}
                    >
                      <option value="">-- Select Parent Company --</option>
                      {parentCompanies.map(pc => (
                        <option key={pc.parent_company_id} value={pc.parent_company_id}>{pc.parent_company_name}</option>
                      ))}
                    </select>
                    {companiesUnderParent.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.75rem', color: '#9CA3AF', fontWeight: '500' }}>
                          {companiesUnderParent.length} companies:
                        </span>
                        {companiesUnderParent.map(c => (
                          <span
                            key={c.company_id}
                            style={{
                              fontSize: '0.75rem',
                              color: '#4B5563',
                              background: '#F3F4F6',
                              padding: '2px 10px',
                              borderRadius: '4px',
                              fontWeight: '500',
                              whiteSpace: 'nowrap'
                            }}
                            title={c.company_name}
                          >
                            {c.company_name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button onClick={handleApplyFilters} className="btn-primary btn-load">Search</button>
              </div>
            </div>

            {/* Date Filters Row */}
            <div className="filters-row" style={{ marginTop: '12px', padding: '12px 16px', background: '#F9FAFB', borderRadius: '6px' }}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Date Presets */}
                <div className="filter-group">
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '12px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    Quick Date:
                  </label>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {DATE_PRESETS.map(([label, key]) => (
                      <button key={key} className="date-preset-btn" onClick={() => setDatePreset(key)}>{label}</button>
                    ))}
                  </div>
                </div>

                {/* Date Range */}
                <div className="filter-group">
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '12px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    Pickup Date:
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="date"
                      value={pickupDateStart}
                      onChange={e => setPickupDateStart(e.target.value)}
                      style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #D1D5DB', fontSize: '13px', background: '#F9FAFB' }}
                    />
                    <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>to</span>
                    <input
                      type="date"
                      value={pickupDateEnd}
                      onChange={e => setPickupDateEnd(e.target.value)}
                      style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #D1D5DB', fontSize: '13px', background: '#F9FAFB' }}
                    />
                  </div>
                </div>

                {/* Clear Button */}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginLeft: 'auto' }}>
                  {(pickupDateStart || pickupDateEnd || selectedWeeks.length > 0) && (
                    <button
                      onClick={handleClearFilters}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: 'none',
                        background: '#FEE2E2',
                        color: '#DC2626',
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '13px'
                      }}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Week Filter Row — matching WeeklyDataMatrix's filters-row */}
            {availableWeeks.length > 0 && (
              <div className="filters-row" style={{ marginTop: '8px', padding: '12px 16px', background: '#F9FAFB', borderRadius: '6px' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Week Pills */}
                  <div className="filter-group">
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '12px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Filter by Week:
                    </label>
                    <div className="dashboard-week-pills">
                      <button
                        className={`week-pill ${selectedWeeks.length === 0 ? 'active' : ''}`}
                        onClick={() => { setSelectedWeeks([]); setLastNWeeks('all'); }}
                      >All Weeks</button>
                      {availableWeeks.map(w => (
                        <button
                          key={w.value}
                          className={`week-pill ${selectedWeeks.includes(w.value) ? 'active' : ''}`}
                          onClick={() => toggleWeek(w.value)}
                        >{w.label}</button>
                      ))}
                    </div>
                  </div>

                  {/* Quick Filter Dropdown */}
                  <div className="filter-group">
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '12px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Quick Filter:
                    </label>
                    <select
                      value={lastNWeeks}
                      onChange={(e) => handleLastNWeeksChange(e.target.value)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid #D1D5DB',
                        fontSize: '13px',
                        fontWeight: '400',
                        cursor: 'pointer',
                        background: '#F9FAFB',
                        minWidth: '150px'
                      }}
                    >
                      <option value="all">All Weeks</option>
                      <option value="4">Last 4</option>
                      <option value="8">Last 8</option>
                      <option value="12">Last 12</option>
                    </select>
                  </div>

                  {/* Week counter + clear */}
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginLeft: 'auto' }}>
                    {selectedWeeks.length > 0 && (
                      <button
                        onClick={() => { setSelectedWeeks([]); setLastNWeeks('all'); }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '4px',
                          border: 'none',
                          background: '#FEE2E2',
                          color: '#DC2626',
                          cursor: 'pointer',
                          fontWeight: '500',
                          fontSize: '13px'
                        }}
                      >
                        Clear Weeks
                      </button>
                    )}
                    <div style={{ color: '#6B7280', fontSize: '12px', fontWeight: '500' }}>
                      {selectedWeeks.length > 0
                        ? `${selectedWeeks.length} of ${availableWeeks.length} weeks selected`
                        : `${availableWeeks.length} weeks available`}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="dashboard-main">
        {activeTab === 'cashflow' && <WeeklyDataMatrix />}
        {activeTab === 'dispatch' && (
          <DispatchAnalysis
            selectedCompany={effectiveCompanyId}
            buildParams={buildParams}
            filterOptions={filterOptions}
            selectedWeeks={selectedWeeks}
            filterVersion={filterVersion}
          />
        )}
        {activeTab === 'broker' && (
          <BrokerAnalysis
            selectedCompany={effectiveCompanyId}
            buildParams={buildParams}
            filterOptions={filterOptions}
            selectedWeeks={selectedWeeks}
            filterVersion={filterVersion}
          />
        )}
        {activeTab === 'lanes' && (
          <LaneAnalysis
            selectedCompany={effectiveCompanyId}
            buildParams={buildParams}
          />
        )}
        {activeTab === 'transactions' && <UserTransactions />}
      </main>
    </div>
  );
}

function formatWeekLabel(start, end) {
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${m[start.getMonth()]} ${start.getDate()}-${end.getDate()}`;
}

export default Dashboard;
